import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

import { CustomTableProvider, useCustomTable } from "./CustomTableContext";
import type { DashboardCell, DashboardRow, DashboardTable } from "./helpers";
import { TableBodyCell } from "./TableBodyCell";
import { TableHeaderCell } from "./TableHeaderCell";

/**
 * Public props for the shared Dashboard table renderer.
 * Row callbacks receive the shared Dashboard row type.
 * @template TColumnData Row data shape used by the provided Dashboard table.
 */
export type TableProps<TColumnData extends {}> = {
  /** Table instance created with the shared Dashboard table features. */
  table: DashboardTable<TColumnData>;
  "data-testid"?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  readOnly?: boolean;
  /** Called with the clicked Dashboard row when row clicks are enabled. */
  onRowClick?: (row: DashboardRow<TColumnData>) => void;
  /** Returns the navigation target for a Dashboard row when rows should behave as links. */
  getRowHref?: (row: DashboardRow<TColumnData>) => string;
};

export function Table<TColumnData extends {}>(props: TableProps<TColumnData>): React.ReactNode {
  return (
    <CustomTableProvider table={props.table}>
      <TableContent {...props} />
    </CustomTableProvider>
  );
}

function TableContent<TColumnData extends {}>({
  table,
  children,
  isLoading,
  readOnly,
  getRowHref,
  onRowClick,
  ...props
}: TableProps<TColumnData>): React.ReactNode {
  const { containerRef, unfilledWidth } = useCustomTable();
  const navigate = useNavigate();

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: DashboardRow<TColumnData>) => {
    const target = e.target;
    if (target instanceof Element && target.closest("a, button")) {
      return;
    }

    if (onRowClick) {
      onRowClick(row);
    }

    if (getRowHref) {
      if (e.ctrlKey || e.metaKey) {
        window.open(getRowHref(row), "_blank");
      } else {
        void navigate(getRowHref(row));
      }
    }
  };

  const rows = table.getRowModel().rows;
  const widthTableContent = table.getTotalSize();
  const widthTable = widthTableContent + unfilledWidth;

  return (
    <div className="relative w-full max-w-full overflow-hidden rounded-table-surface border border-border-default bg-background-default">
      <div className="relative w-full max-w-full overflow-auto overscroll-x-none" ref={containerRef}>
        <RuntimeWidth width={widthTable}>
          {(widthAttributes) => (
            <table
              {...props}
              {...widthAttributes}
              className="m-table-reset table-fixed overflow-hidden p-table-reset text-caption"
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    className="group/header border-b border-b-border-default bg-background-default"
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((header, index, headers) => (
                      <TableHeaderCell key={header.id} header={header} isLastColumn={index === headers.length - 1} />
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className={isLoading ? "pointer-events-none" : undefined}>
                {isLoading ? (
                  <>
                    {Array.from({ length: 20 }, (_, i) => `skeleton-row-${i}`).map((id) => (
                      <tr key={id} className="border-b border-b-border-default bg-background-default">
                        {table.getVisibleFlatColumns().map((column, index, columns) => (
                          <TableBodyCell
                            key={column.id}
                            isLastColumn={index === columns.length - 1}
                            // Mock cell data for loading state
                            cell={
                              {
                                column,
                                row: {} as DashboardRow<TColumnData>,
                                id: "",
                                renderValue: () => ({}) as any,
                                getIsAggregated: () => false,
                                getIsPlaceholder: () => false,
                                getIsGrouped: () => false,
                                getValue: () => null as any,
                                getContext: () => ({}) as any,
                              } as unknown as DashboardCell<TColumnData>
                            }
                            isLoading
                          />
                        ))}
                      </tr>
                    ))}
                  </>
                ) : (
                  rows.map((row) => {
                    const rowHref = getRowHref?.(row);
                    const isRowClickable = (onRowClick || rowHref) && !readOnly;

                    return (
                      <tr
                        data-testid="table-row"
                        key={row.id}
                        className={twJoin(
                          "group border-b border-b-border-default last:border-b-0 odd:bg-background-default even:bg-background-on-default",
                          isRowClickable &&
                            "duration-table-interaction cursor-pointer transition-colors hover:bg-background-secondary",
                        )}
                        onClick={isRowClickable ? (e) => handleRowClick(e, row) : undefined}
                      >
                        {row.getVisibleCells().map((cell, index, cells) => (
                          <TableBodyCell
                            key={cell.id}
                            cell={cell}
                            isLastColumn={index === cells.length - 1}
                            onRowClick={onRowClick}
                            rowHref={getRowHref?.(row)}
                            isLoading={isLoading}
                          />
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </RuntimeWidth>
        {children ? (
          <div className="sticky left-0 border-t border-t-border-default bg-background-default p-space-md">
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
